/**
 * EditorDocument → PDF export. Single-page PDF sized to the canvas
 * dimensions, with the canvas embedded as a JPEG image at quality 92.
 *
 * Unlike PSD / SVG (which preserve layer structure), PDF v1 is a
 * flat export — the editor's composited canvas is rendered once and
 * embedded as a single image stream. The structured-format treatment
 * (local-only destination, no quality slider) is consistent with
 * PSD / SVG; the lack of layer preservation is intentional. PDF's
 * vector / text models differ enough from PSD's that a faithful
 * round-trip would be a separate large piece of work.
 *
 * Dynamic-imports jspdf to keep non-PDF users off the heavy bundle
 * — same strategy as `psd-export.ts`'s ag-psd import. jsPDF in this
 * repo is `^4.2.1` and ships native ESM types.
 */

import type { EditorDocument } from '../types';
import { pxToPoints } from './dpi-conversion';

/** JPEG quality used when embedding the canvas in the PDF. PDFs
 *  commonly carry JPEG-compressed photos; using JPEG keeps the
 *  output file size reasonable for the typical photo case. Lossless
 *  PNG embedding is supported by jsPDF but would inflate file sizes
 *  several-fold for high-res images. 0.92 matches the editor's
 *  default export quality elsewhere (JPEG / WebP slider default). */
export const PDF_IMAGE_JPEG_QUALITY = 0.92;

export interface ExportDocumentToPdfOptions {
  documentId: string;
  document: EditorDocument;
  canvasWidth: number;
  canvasHeight: number;
  /** Document DPI. Tier 3 Group D Slice 1d — when provided, the
   *  PDF page is sized in PostScript points (1 in = 72 pt) so the
   *  page's physical dimensions match the user's intended print
   *  size. Omitted or `null` keeps the pixel-as-unit legacy path
   *  for byte-identical back-compat with pre-slice-1d exports. */
  dpi?: number | null;
  /** Data URL of the composited canvas. Should be a JPEG data URL
   *  (e.g. `canvas.toDataURL('image/jpeg', 0.92)`) so jsPDF can
   *  embed it directly without re-encoding. Passing a PNG also
   *  works — jsPDF will embed it as-is when the caller signals
   *  `'PNG'` format, but our orchestrator forces JPEG embedding
   *  for size reasons (see PDF_IMAGE_JPEG_QUALITY above). */
  canvasDataUrl: string;
}

export interface ExportDocumentToPdfResult {
  /** PDF bytes ready for download / Fias upload / etc. */
  bytes: ArrayBuffer;
}

/** Choose page orientation from canvas aspect. jsPDF treats this as
 *  a metadata hint when `format` is an explicit `[w, h]` array; the
 *  page dimensions still come from the array. Picking the orientation
 *  that matches the aspect avoids a no-op rotation step in some
 *  jsPDF rendering paths. */
function orientationFor(width: number, height: number): 'landscape' | 'portrait' {
  return width >= height ? 'landscape' : 'portrait';
}

/** Bytes-out entry point. Embeds the supplied canvas data URL as a
 *  single PDF page sized exactly to the canvas dimensions in pixels.
 *  jsPDF accepts arbitrary pixel units when constructed with
 *  `unit: 'px'`; the `px_scaling` hotfix corrects ~1px rounding
 *  asymmetry that appears on some square pages in stock jsPDF
 *  builds. */
export async function exportDocumentToPdf(
  opts: ExportDocumentToPdfOptions,
): Promise<ExportDocumentToPdfResult> {
  const { canvasWidth, canvasHeight, canvasDataUrl, dpi } = opts;
  if (!Number.isFinite(canvasWidth) || !Number.isFinite(canvasHeight)) {
    throw new Error(
      `exportDocumentToPdf: invalid canvas dimensions ${canvasWidth}x${canvasHeight}`,
    );
  }
  if (canvasWidth < 1 || canvasHeight < 1) {
    throw new Error(
      `exportDocumentToPdf: canvas dimensions must be ≥1 (got ${canvasWidth}x${canvasHeight})`,
    );
  }
  // The orchestrator hard-codes the JPEG embed path. Accepting a non-
  // JPEG data URL would still produce a valid PDF (jsPDF re-encodes
  // PNG → JPEG internally when told `format: 'JPEG'`), but the
  // round-trip is wasteful and the caller probably intended otherwise.
  // Reject explicitly so a future regression in the caller doesn't
  // silently inflate output sizes 3-5×. (Pre-PR review audit Critical #1.)
  if (!canvasDataUrl.startsWith('data:image/jpeg')) {
    throw new Error(
      'exportDocumentToPdf: canvasDataUrl must be a JPEG data URL (call canvas.toDataURL("image/jpeg", PDF_IMAGE_JPEG_QUALITY) first)',
    );
  }
  // Tier 3 Group D Slice 1d — when a DPI is declared, size the page
  // in PostScript points so the physical print dimensions match the
  // user's intended print size. Documents with no DPI declared
  // (`dpi == null` from a freshly-imported web image or a legacy
  // session record) stay on the pixel-as-unit path so existing
  // exports remain byte-identical with the pre-slice-1d behavior.
  const useDpiAwarePage = dpi != null && typeof dpi === 'number' && Number.isFinite(dpi) && dpi > 0;
  const pageUnit: 'pt' | 'px' = useDpiAwarePage ? 'pt' : 'px';
  const pageW = useDpiAwarePage ? pxToPoints(canvasWidth, dpi) : canvasWidth;
  const pageH = useDpiAwarePage ? pxToPoints(canvasHeight, dpi) : canvasHeight;

  let bytes: ArrayBuffer;
  try {
    const { jsPDF } = await import('jspdf');
    const doc = new jsPDF({
      unit: pageUnit,
      format: [pageW, pageH],
      orientation: orientationFor(pageW, pageH),
      hotfixes: ['px_scaling'],
    });
    // addImage args: (imageData, format, x, y, width, height, alias,
    // compression). 'JPEG' embeds via JPEG stream — the caller pre-
    // encodes the canvas as JPEG (validated above). 'FAST' compression
    // is fine for a single image; MEDIUM / SLOW pay off for very
    // large multi-image documents we don't produce here.
    doc.addImage(canvasDataUrl, 'JPEG', 0, 0, pageW, pageH, undefined, 'FAST');
    bytes = doc.output('arraybuffer') as ArrayBuffer;
  } catch (err) {
    // jsPDF can throw on malformed dataUrls, OOM on very large
    // images, or version-skew between our typings + the runtime
    // build. Wrap so callers get a typed message they can surface
    // in a toast rather than a generic stack trace. (Pre-PR review
    // audit Critical #1.)
    const inner = err instanceof Error ? err.message : String(err);
    throw new Error(`exportDocumentToPdf: jsPDF failed (${inner})`);
  }
  return { bytes };
}

/** Encode a single canvas as a PDF Blob — used by the slice export
 *  pipeline, where each slice becomes its own .pdf inside the ZIP.
 *  The slice's canvas is the source; the PDF page is sized to the
 *  slice's pixel dimensions. The canvas is JPEG-encoded internally
 *  at PDF_IMAGE_JPEG_QUALITY so slice callers don't have to manage
 *  the per-slice dataUrl conversion. */
export async function encodeCanvasAsPdfBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  const dataUrl = canvas.toDataURL('image/jpeg', PDF_IMAGE_JPEG_QUALITY);
  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({
    unit: 'px',
    format: [canvas.width, canvas.height],
    orientation: orientationFor(canvas.width, canvas.height),
    hotfixes: ['px_scaling'],
  });
  doc.addImage(dataUrl, 'JPEG', 0, 0, canvas.width, canvas.height, undefined, 'FAST');
  return doc.output('blob');
}
